'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import * as z from 'zod';

import type { EmergencyContact } from '~entities/student';

import { useGetCurrency } from '~/shared/api/currency-api';
import { getCountryCodeFromCurrency } from '~/shared/utils/currency-country';
import { phoneInputValueToE164 } from '~/shared/utils/phone-input-e164';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';

const formSchema = z.object({
  firstName: z.string().min(2, {
    message: 'First name must be at least 2 characters.',
  }),
  lastName: z.string().min(2, {
    message: 'Last name must be at least 2 characters.',
  }),
  authorized: z.boolean(),
  phone: z.string({
    message: 'Please enter a valid phone number.',
  }),
  emergencyContactEmail: z
    .string()
    .email({
      message: 'Please enter a valid email address.',
    })
    .or(z.literal('')),
});

export type EmergencyContactFormSchema = z.infer<typeof formSchema>;

export function EmergencyContactForm({
  setIsEditing,
  currentContact,
  onSubmit,
  isPending,
}: {
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  currentContact?: EmergencyContact;
  onSubmit: (values: EmergencyContactFormSchema) => void;
  isPending: boolean;
}) {
  const form = useForm<EmergencyContactFormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: currentContact?.firstName ?? '',
      lastName: currentContact?.lastName ?? '',
      authorized: currentContact?.authorized ?? false,
      phone: currentContact?.phone ?? '',
      emergencyContactEmail: currentContact?.emergencyContactEmail ?? '',
    },
  });

  const { data: currencyData } = useGetCurrency();
  const defaultCountry = getCountryCodeFromCurrency(currencyData?.name);

  return (
    <Card className=''>
      <CardHeader>
        <CardTitle>Edit Emergency Contact</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
            <div className='grid grid-cols-2 gap-4'>
              <FormField
                control={form.control}
                name='firstName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input placeholder='John' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='lastName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input placeholder='Doe' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone number</FormLabel>
                  <FormControl>
                    {/* <Input placeholder='+1234567890' {...field} /> */}
                    <PhoneInput
                      country={defaultCountry}
                      disableCountryGuess={true}
                      value={field.value || ''}
                      onChange={(value) => {
                      field.onChange(phoneInputValueToE164(value));
                    }}
                      inputStyle={{ width: '100%' }}
                      enableSearch={true}
                      searchPlaceholder='Search country...'
                      searchNotFound='No country found'
                      enableAreaCodes={true}
                      countryCodeEditable={true}
                      placeholder='Enter mobile number'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='emergencyContactEmail'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder='john.doe@example.com' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='authorized'
              render={({ field }) => (
                <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      // {...form.register('authorized')}
                    />
                  </FormControl>
                  <div className='space-y-1 leading-none'>
                    <FormLabel>Authorized pickup</FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </form>
        </Form>
      </CardContent>
      <CardFooter className='flex items-center justify-end gap-2'>
        <Button
          type='button'
          variant={'outline'}
          onClick={() => {
            setIsEditing(false);
          }}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          className=''
          onClick={form.handleSubmit(onSubmit)}
        >
          {isPending ? <Loader2 className='animate-spin' /> : 'Submit'}
        </Button>
      </CardFooter>
    </Card>
  );
}
